import React, { useContext, useState, useEffect, useRef, useMemo } from "react";
import {
  Container,
  AppBar,
  Toolbar,
  Typography,
  Button,
  IconButton,
  Box,
  Drawer,
  Link,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  useMediaQuery,
  Menu,
  MenuItem,
  Avatar,
  Slide,
  Divider,
  Tooltip,
  Grid,
  Tabs,
  Tab,
  Link as MUILink,
  useTheme,
  Fade,
} from "@mui/material";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import { CircleUserRound } from "lucide-react";

import HelpOutlineIcon from "@mui/icons-material/HelpOutline";
import CloseIcon from "@mui/icons-material/Close";
import AdminPanelSettingsIcon from "@mui/icons-material/AdminPanelSettings";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import { supabase } from "../../../supabase/supabaseConfig";
import { signOut } from "../../../supabase/supabaseAuth";

import { useRouter, usePathname } from "next/navigation";

import CustomTooltip from "@/components/atoms/CustomTooltip";
import { useDeviceType } from "@/hooks/useDeviceType";
import { useOnlineStatus } from "@/hooks/useOnlineStatus";
import { useAudioContext } from "@/context/AudioContext";
import { useQueryClient } from '@tanstack/react-query';
import { ProfileMenu } from '../menus/ProfileMenu';
import { 
  getAuthenticatedNavLinks, 
  getUnauthenticatedNavLinks,
  isValidNavLink 
} from "./AppHeader.config";
// import { isPWAMode } from "@/utilities/pwaDetection"; // Currently unused after removing early access
// import { useUI } from "@/context/UIContext"; // Currently unused after removing early access

const LyricBoostLogo = "/logos/lyric-boost-logo.svg";
const LyricBoostTextLogo = "/logos/lyric-boost-text-logo.png";

//import AvatarImage from "/avatars/lb-avatar.png";

const AvatarImage = "/avatars/lb-avatar.png";

interface AppHeaderProps {
  showIconLogo?: boolean;
  showTextLogo?: boolean;
  logoVisibility?: any;
  user?: any;
  titleContainer?: React.ReactNode;
  subtitleContainer?: React.ReactNode;
  actionButtons?: React.ReactNode;
  tabs?: React.ReactNode;
  isLoggedIn?: boolean;
  includeProfileMenu?: boolean;
  showMobileMenuIcon?: boolean;
  desktopNavLinks?: any[];
  mobileNavLinks?: any[];
  isLoadingAuth?: boolean; // Add loading prop from parent
  showBackButton?: boolean; // Explicitly control back button independently of logo
}

const AppHeader: React.FC<AppHeaderProps> = ({
  showIconLogo,
  showTextLogo,
  logoVisibility,
  user,
  titleContainer,
  subtitleContainer,
  actionButtons,
  tabs,
  isLoggedIn = user ? true : false,
  includeProfileMenu = true,
  showMobileMenuIcon = true,
  desktopNavLinks = null,
  mobileNavLinks = null,
  isLoadingAuth = false,
  showBackButton = true, // Default to true, back button shows when backLink exists
}) => {
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [mounted, setMounted] = useState(false);

  // Ensure consistent hydration by only checking loading state after mount
  useEffect(() => {
    setMounted(true);
  }, []);

  const theme = useTheme();
  const queryClient = useQueryClient();
  const isOnline = useOnlineStatus();
  const { stopAudio } = useAudioContext();

  const { isMobileOrTablet, isDesktop } = useDeviceType();

  const effectiveShowIconLogo = useMemo(() => {
    return logoVisibility
      ? isMobileOrTablet
        ? logoVisibility.icon?.mobile !== false
        : logoVisibility.icon?.desktop !== false
      : showIconLogo !== false;
  }, [logoVisibility, isMobileOrTablet, showIconLogo]);

  // Intelligent text logo display: never show text logo when custom title is present
  // The full "Lyric Boost" text logo should not coexist with custom page titles
  // Check for actual content (not just empty strings or whitespace)
  const hasCustomTitle = useMemo(() => {
    return titleContainer && (
      typeof titleContainer !== 'string' || titleContainer.trim().length > 0
    );
  }, [titleContainer]);
  
  const effectiveShowTextLogo = useMemo(() => {
    return hasCustomTitle 
      ? false 
      : (logoVisibility
          ? isMobileOrTablet
            ? logoVisibility.text?.mobile !== false
            : logoVisibility.text?.desktop !== false
          : showTextLogo === true);
  }, [hasCustomTitle, logoVisibility, isMobileOrTablet, showTextLogo]);

  const headerRef = useRef(null);
  const [headerHeight, setHeaderHeight] = useState(64); // Default height

  const [visible, setVisible] = useState(true);
  const [lastScrollY, setLastScrollY] = useState(0);
  const [anchorEl, setAnchorEl] = useState(null);
  const open = Boolean(anchorEl);

  // Use the universal dialog system (currently unused after removing early access)
  // const { showOverlay } = useUI();

  const router = useRouter();
  const pathname = usePathname();

  const [showHeaderShadow, setShowHeaderShadow] = useState(true);

  const [activeTab, setActiveTab] = useState(0); // State for active tab

  // Measure header height once on mount
  useEffect(() => {
    if (headerRef.current) {
      setHeaderHeight(headerRef.current.getBoundingClientRect().height);
    }
  }, []);

  useEffect(() => {
    const handleScroll = () => {
      const isOnHomePage = pathname === "/";
      const isAtTop = window.scrollY < 10;

      if (isOnHomePage && isAtTop) {
        setShowHeaderShadow(false);
      } else {
        setShowHeaderShadow(true);
      }

      if (window.scrollY < 50) {
        setVisible(true); // Always show when near the top
      } else if (window.scrollY > lastScrollY) {
        setVisible(false); // Hide when scrolling down
      } else {
        setVisible(true); // Show when scrolling up
      }
      setLastScrollY(window.scrollY);
    };

    window.addEventListener("scroll", handleScroll);
    return () => {
      window.removeEventListener("scroll", handleScroll);
    };
  }, [lastScrollY]);

  const handleTabChange = (event, newValue) => {
    setActiveTab(newValue);
  };

  const [profile, setProfile] = useState({
    username: "",
    stage_name: "",
    legal_name: "",
    email: "",
    avatar: "",
    avatar_url: "",
    bio: "",
    genres: [],
    song_subject_matter: [],
    artist_inspirations: [],
    social_links: {},
    created_at: "",
  });

  // Track if we've already fetched for this user email to prevent refetching
  const [fetchedEmail, setFetchedEmail] = useState<string | null>(null);

  useEffect(() => {
    const fetchUserData = async () => {
      if (user?.email && user.email !== fetchedEmail) {
        const { data, error } = await supabase
          .from("profiles")
          .select("*")
          .eq("email", user.email)
          .single();

        if (data) {
          setProfile(data);
          setFetchedEmail(user.email);
        }
        if (error) console.error("Error fetching profile:", error.message);
      }
    };
    fetchUserData();
  }, [user?.email, fetchedEmail]);

  // For inline song title editing
  const [isEditingSongTitle, setIsEditingSongTitle] = useState(false);
  const [songTitle, setSongTitle] = useState(titleContainer || "");

  // Update local state if the titleContainer prop changes
  useEffect(() => {
    setSongTitle(titleContainer || "");
  }, [titleContainer]);

  // Function to update the song title in Supabase
  // const updateSongTitleInDB = async () => {
  //   if (!songId) return;
  //   const { data, error } = await supabase
  //     .from("songs")
  //     .update({ title: songTitle })
  //     .eq("id", songId);
  //   if (error) {
  //     console.error("Error updating song title:", error.message);
  //   } else {
  //     console.log("Song title updated", data);
  //   }
  // };

  // Handle scrolling behavior
  useEffect(() => {
    const handleScroll = () => {
      if (window.scrollY < 100) {
        setVisible(true); // Always show when near the top
      } else if (window.scrollY > lastScrollY) {
        setVisible(false); // Hide when scrolling down
      } else {
        setVisible(true); // Show when scrolling up
      }
      setLastScrollY(window.scrollY);
    };

    window.addEventListener("scroll", handleScroll);
    return () => {
      window.removeEventListener("scroll", handleScroll);
    };
  }, [lastScrollY]);

  // Determine the back link based on the current route
  const getBackMUILink = () => {
    if (pathname?.includes("edit-song")) {
      return "/songs";
    }

    if (pathname?.includes("create-song")) {
      return "/songs";
    }

    if (pathname?.includes("account/profile")) {
      return isMobileOrTablet ? "/account" : "/songs";
    }

    if (pathname?.includes("account/settings")) {
      return "/account";
    }

    // Check for exact /account path (with or without trailing slash, no subpaths)
    if (pathname === "/account" || pathname === "/account/") {
      return "/songs";
    }

    if (pathname?.includes("ideas")) {
      return "/songs";
    }

    return null; // No back link
  };

  const backLink = getBackMUILink();

  const handleBackButtonClick = () => {
    if (backLink) {
      // Stop audio playback when navigating away from edit-song page
      if (pathname?.includes('/edit-song')) {
        stopAudio();
      }
      
      // If online and navigating back to /songs, invalidate cache to fetch fresh data
      // This ensures new songs appear immediately after creation
      // Offline cache is preserved (only invalidate when online)
      if (isOnline && backLink === "/songs") {
        queryClient.invalidateQueries({ queryKey: ['songs'] });
      }
      router.push(backLink);
    }
  };

  const handleLogout = async () => {
    const { error } = await signOut();
    if (error) {
      console.error("Error logging out: ", error);
    } else {
      // Clear local storage and redirect
      localStorage.clear();
      sessionStorage.clear();
      window.location.replace("/login");
    }
  };

  const handleMenuClick = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
  };

  const toggleDrawer = () => {
    setDrawerOpen(!drawerOpen);
  };

  // Use custom navigation links or fall back to defaults
  // Ensure we always have an array, even if empty object or invalid value is passed
  // Memoize to prevent creating new array references on every render
  const desktopNavigationLinks = useMemo(() => {
    return (Array.isArray(desktopNavLinks) ? desktopNavLinks : null) || 
      (user 
        ? getAuthenticatedNavLinks(handleLogout, router)
        : getUnauthenticatedNavLinks()
      );
  }, [desktopNavLinks, user, router]);
  
  const mobileNavigationLinks = useMemo(() => {
    return (Array.isArray(mobileNavLinks) ? mobileNavLinks : null) || desktopNavigationLinks;
  }, [mobileNavLinks, desktopNavigationLinks]);

  // Memoize computed user values to prevent rerenders
  const userPreferredName = useMemo(() => {
    return profile.stage_name || profile.username || profile.legal_name;
  }, [profile.stage_name, profile.username, profile.legal_name]);
  
  const userAvatar = useMemo(() => {
    return profile.avatar_url || profile.avatar || AvatarImage;
  }, [profile.avatar_url, profile.avatar]);

  const TabRow = ({ tabs }) => {
    // Render the tabs
    // Safety check: ensure tabs is an array
    if (!Array.isArray(tabs) || tabs.length === 0) return null;
    
    return (
      <Box sx={{ mt: 1, borderBottom: 1, borderColor: "divider" }}>
        <Tabs
          value={activeTab}
          onChange={handleTabChange}
          TabIndicatorProps={{
            sx: {
              backgroundColor: "#ecf0f1",
            },
          }}
        >
          {tabs.map((tab, index) => (
            <Tab
              sx={{
                flex: isMobileOrTablet && 1,
                padding: "12px 40px",
                fontSize: "16px",
                textTransform: "none",
                color: "#aabbbf !important",
                "&.Mui-selected": {
                  color: "#fff !important",
                },
                "& :hover": {
                  color: "#fff !important",
                },
              }}
              key={index}
              label={tab.label}
            />
          ))}
        </Tabs>
      </Box>
    );
  };

  const TitleSectionWrapper = () => {
    return (
      <Box id="title-section-wrapper">
        <Grid
          container
          justifyContent="space-between"
          sx={{
            display: "inline-flex",
            mb: 0,
            alignItems: "center",
          }}
        >
          <Grid item xs sm={6}>
            <Box
              sx={{
                display: "flex",
                justifyContent: "flex-start",
                flexDirection: "column",
                textAlign: "left",
              }}
            >
              {typeof titleContainer === "string" ? (
                <Box
                  id="app-header__page-title-container"
                >
                  <Typography
                    id="app-header__page-title"
                    component="h1"
                    variant="h6"
                    sx={{ 
                      lineHeight: 1.2, 
                      fontWeight: 700,
                    }}
                  >
                    {titleContainer}
                  </Typography>
                </Box>
              ) : (
                titleContainer
              )}
              {typeof subtitleContainer === "string" ? (
                <Box
                  style={{
                    color: "#aabbbf !Important",
                    display: "flex",
                    alignItems: "center",
                    gap: "15px",
                  }}
                >
                  <Typography sx={{ fontSize: "14px", color: "#aabbbf" }}>
                    {subtitleContainer}
                  </Typography>
                </Box>
              ) : (
                subtitleContainer
              )}
            </Box>
          </Grid>
          {isMobileOrTablet && Array.isArray(actionButtons) && actionButtons.length > 0 && (
            <Grid item xs={null} sm={4}>
              <Box
                display="flex"
                justifyContent="flex-end"
                flexDirection="row"
                alignItems="center"
                gap={1.5}
              >
                {actionButtons.map((singleButton, index) => {
                  // If the icon is already a complete component (like ExportButton),
                  // render it directly without wrapping but wrap in clickable Box if onClick exists
                  if (singleButton.isCompleteComponent) {
                    if (singleButton.onClick) {
                      return (
                        <Box
                          key={index}
                          onClick={singleButton.onClick}
                          sx={{ display: 'flex', alignItems: 'center' }}
                        >
                          {singleButton.icon}
                        </Box>
                      );
                    }
                    return (
                      <Box key={index}>
                        {singleButton.icon}
                      </Box>
                    );
                  }
                  
                  // Otherwise, wrap in IconButton for simple icons
                  return (
                    <IconButton
                      key={index}
                      onClick={singleButton.onClick}
                      sx={{ textTransform: "none", color: "#ffffff" }}
                      edge="end"
                    >
                      {singleButton.icon}
                    </IconButton>
                  );
                })}
              </Box>
            </Grid>
          )}
        </Grid>
      </Box>
    );
  };

  return (
    <Slide direction="down" in={visible} timeout={300}>
      <AppBar
        ref={headerRef}
        position="fixed"
        elevation={showHeaderShadow ? 1 : 0}
        sx={{
          backgroundColor: showHeaderShadow ? "#111827" : "transparent",
          color: "#ffffff",
          transition: "0.3s ease-in-out",
          padding: "8px 0px",
          paddingTop: 'calc(8px + env(safe-area-inset-top))',
          minHeight: '50px',
          top: 0,
        }}
        suppressHydrationWarning
      >
        <Container maxWidth={false} sx={{ width: '100%' }} suppressHydrationWarning>
          <Toolbar 
            style={{ 
              padding: isMobileOrTablet ? "0px 0px" : "8px 0px", 
              minHeight: 0 
            }}
          >
            <Grid
              container
              sx={{ alignItems: "center", justifyContent: "space-between", gap: 1.5 }}
            >
              {/* Full header content */}
              <Grid
                container
                sx={{ 
                  alignItems: "center", 
                  justifyContent: "space-between", 
                  gap: 1.5,
                  display: (!mounted || isLoadingAuth) ? 'none' : 'flex'
                }}
              >
                    {showBackButton && backLink && (
                  <Grid item xs={null}>
                    <IconButton
                      edge="start"
                      color="inherit"
                      onClick={handleBackButtonClick}
                      size={isMobileOrTablet ? "small" : "medium"}
                      sx={{ 
                        ...(isMobileOrTablet && {
                          padding: '6px',
                        })
                      }}
                    >
                      <ArrowBackIcon fontSize={isMobileOrTablet ? "small" : "medium"} />
                    </IconButton>
                  </Grid>
                )}

                {(effectiveShowIconLogo || effectiveShowTextLogo) && (
                  <Grid item id="logo-column" xs={null}>
                    <Box
                      display="flex"
                      alignItems="center"
                      style={{ flexGrow: 1 }}
                    >
                      <CustomTooltip
                        title={!showTextLogo && "Lyric Boost"}
                        placement="bottom"
                      >
                        <Link
                          onClick={() =>
                            router.push(isLoggedIn ? "/songs" : "/")
                          }
                          sx={{
                            color: "#ffffff",
                            display: "flex",
                            alignItems: "center",
                            cursor: "pointer",
                            padding: "0px 5px",
                          }}
                        >
                          {effectiveShowIconLogo && (
                            <Box sx={{ display: "flex", alignItems: "center" }}>
                              <img
                                src={LyricBoostLogo}
                                alt="Lyric Boost icon logo"
                                style={{ maxHeight: "40px", willChange: "auto" }}
                              />
                            </Box>
                          )}
                          {effectiveShowTextLogo && (
                            <Box sx={{ display: "flex", alignItems: "center", ml: 1 }}>
                              <img
                                src={LyricBoostTextLogo}
                                alt="Lyric Boost"
                                style={{ maxHeight: "24px", willChange: "auto" }}
                              />
                            </Box>
                          )}
                        </Link>
                      </CustomTooltip>
                    </Box>
                  </Grid>
                )}

                <Grid item id="title-column" xs>
                  <TitleSectionWrapper />
                </Grid>

                {/* Desktop action buttons */}
                {!isMobileOrTablet && Array.isArray(actionButtons) && actionButtons.length > 0 && (
                  <Grid item xs={null}>
                    <Box
                      display="flex"
                      justifyContent="flex-end"
                      flexDirection="row"
                      alignItems="center"
                      gap={1.5}
                      sx={{ mr: 2 }}
                    >
                      {actionButtons.map((singleButton, index) => {
                        // If the icon is already a complete component (like ExportButton),
                        // render it directly without wrapping but wrap in clickable Box if onClick exists
                        if (singleButton.isCompleteComponent) {
                          if (singleButton.onClick) {
                            return (
                              <Box
                                key={index}
                                onClick={singleButton.onClick}
                                sx={{ display: 'flex', alignItems: 'center' }}
                              >
                                {singleButton.icon}
                              </Box>
                            );
                          }
                          return (
                            <Box key={index}>
                              {singleButton.icon}
                            </Box>
                          );
                        }
                        
                        // Otherwise, wrap in IconButton for simple icons
                        return (
                          <IconButton
                            key={index}
                            onClick={singleButton.onClick}
                            sx={{ textTransform: "none", color: "#ffffff" }}
                            edge="end"
                          >
                            {singleButton.icon}
                          </IconButton>
                        );
                      })}
                    </Box>
                  </Grid>
                )}

                <>
                  {isMobileOrTablet ? (
                    <>
                      {includeProfileMenu && showMobileMenuIcon && (
                        <Grid item xs={null}>
                          <IconButton
                            edge="end"
                            color="inherit"
                            onClick={() => router.push("/account")}
                            size={isMobileOrTablet ? "small" : "medium"}
                            sx={{ 
                              ...(isMobileOrTablet && {
                                padding: '6px',
                              })
                            }}
                          >
                            <CircleUserRound size={22} />
                          </IconButton>
                        </Grid>
                      )}
                    </>
                  ) : (
                    <Grid item xs={null}>
                      <Box>
                        {isLoggedIn && includeProfileMenu && pathname !== '/welcome' ? (
                          <ProfileMenu
                            user={user}
                            profile={profile}
                            userAvatar={userAvatar}
                            userPreferredName={userPreferredName}
                            onLogout={handleLogout}
                          />
                        ) : (
                          // Show login/signup buttons for non-logged-in users
                          desktopNavigationLinks
                            .filter(isValidNavLink)
                            .map((link, index) => (
                              <Button
                                key={index}
                                component={link.to ? MUILink : "button"}
                                href={link.to}
                                color={link.color || "inherit"}
                                variant={link.variant || "text"}
                                onClick={link.onClick}
                                sx={{
                                  textTransform: "none",
                                  marginRight: "10px",
                                }}
                              >
                                {link.label}
                              </Button>
                            ))
                        )}
                      </Box>
                    </Grid>
                  )}
                </>
              </Grid>
            </Grid>
          </Toolbar>

          <Drawer
              anchor="right"
              open={drawerOpen}
              onClose={toggleDrawer}
              sx={{
                [`& .MuiDrawer-paper`]: {
                  width: "240px",
                  backgroundColor: "#111827",
                  color: "#ffffff",
                },
              }}
            >
              {isLoggedIn && (
                <Box
                  sx={{
                    py: 4,
                    px: 2,
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "center",
                  }}
                >
                  <Box
                    className="profile-avatar"
                    sx={{
                      width: "56px",
                      height: "56px",
                      overflow: "hidden",
                      borderRadius: "100%",
                      backgroundImage: `url(${userAvatar})`,
                      backgroundSize: "cover",
                      backgroundPosition: "center",
                    }}
                  />
                  {userPreferredName && (
                    <Typography variant="subtitle1" sx={{ mt: 2 }}>
                      {userPreferredName}
                    </Typography>
                  )}
                  <Typography variant="body2">{user?.email}</Typography>
                </Box>
              )}
              <Divider sx={{ margin: "0px !Important" }} />
              <List>
                {mobileNavigationLinks
                  .filter(isValidNavLink)
                  .map((link, index) => {
                    // Render divider if specified
                    if (link.divider) {
                      return <Divider key={`divider-${index}`} sx={{ margin: "8px 0 !Important" }} />;
                    }
                    
                    return (
                      <ListItem
                        key={index}
                        onClick={async (e) => {
                          e.preventDefault(); // Prevent default link behavior
                          if (link.onClick) {
                            await link.onClick(); // Wait for logout to finish
                          }
                          if (link.to && !link.onClick) {
                            router.push(link.to); // Navigate only if it's a link and not a logout button
                          } else {
                            toggleDrawer(); // Just close the drawer for non-navigation actions
                          }
                        }}
                        sx={{ cursor: 'pointer' }}
                      >
                        {link.icon && (
                          <ListItemIcon sx={{ color: "#ffffff" }}>
                            {link.icon}
                          </ListItemIcon>
                        )}
                        <ListItemText
                          primary={link.label}
                          primaryTypographyProps={{
                            style: { color: "#ffffff" },
                          }}
                        />
                      </ListItem>
                    );
                  })}
              </List>
            </Drawer>
          </Container>
      </AppBar>
    </Slide>
  );
};

export default React.memo(AppHeader);
